// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  type AxiosInstance,
  type AxiosRequestHeaders,
  type InternalAxiosRequestConfig,
} from 'axios';
import { selectToken, selectAuthUser } from '@shared/data-access/slices/auth';
import { isUndefined } from '@shared/utils';
import type { RootAuthState } from '@shared/data-access/slices/auth/auth-state';

export type GetAuthState = () => RootAuthState;

export type GetAuthStateOrPromise = () => RootAuthState | Promise<RootAuthState>;

export type ReduxAuthInterceptor = (
  config: InternalAxiosRequestConfig,
  getState: GetAuthState,
) => InternalAxiosRequestConfig | Promise<InternalAxiosRequestConfig>;

export const getAuthStateLazy = async (): Promise<RootAuthState> => {
  const { appStore } = await import('@advisor-app/store');

  return appStore.getState() as unknown as RootAuthState;
};

export const setupAuthInterceptor = (
  axiosInstance: AxiosInstance,
  getState: GetAuthStateOrPromise,
  ...interceptors: ReduxAuthInterceptor[]
): AxiosInstance => {
  axiosInstance.interceptors.request.use(config => {
    const url = config.url;

    if (url && url[url.length - 1] !== '/') {
      config.url = `${config.url}/`;
    }

    return config;
  });

  interceptors.forEach(interceptor => {
    axiosInstance.interceptors.request.use(async config => {
      const state = await Promise.resolve(getState());
      const getStateSync = (): RootAuthState => state;

      return interceptor(config, getStateSync);
    });
  });

  return axiosInstance;
};

export const useAuthTokenInterceptor: ReduxAuthInterceptor = (config, getState) => {
  const token = selectToken(getState());

  if (token) {
    config.headers = {
      ...config.headers,
      Authorization: `Bearer ${token}`,
    } as AxiosRequestHeaders;
  }

  return config;
};

export const useAuthEmailQueryParamInterceptor: ReduxAuthInterceptor = (
  config,
  getState,
) => {
  if (!isUndefined(config.params?.email) || !isUndefined(config.data?.email)) {
    const user = selectAuthUser(getState());
    const email = user?.email;

    switch (config.method?.toLowerCase()) {
      case 'get':
      case 'delete':
        config.params = { ...config.params, email };
        break;
      case 'post':
      case 'put':
        config.data = { ...config.data, email };
        break;
    }
  }

  return config;
};

export const useAuthUserQueryParamInterceptor: ReduxAuthInterceptor = (
  config,
  getState,
) => {
  if (!config.url?.includes('settings')) {
    const user = selectAuthUser(getState());

    config.params = {
      ...config.params,
      user: user?.email,
    };
  }

  return config;
};
